<!-- Skip to main content (WCAG 2.4.1) -->
<a href="#main-content" class="skip-link">{{
    'common.skipToMain' | transloco
}}</a>

<!-- Loading bar (PFL-1679: auto mode is disabled app-wide in app.config.ts;
     bound here explicitly so this shell layout can never drift back to the
     default true and re-stack on top of MetricSurface) -->
<fuse-loading-bar [autoMode]="false"></fuse-loading-bar>

<!-- Navigation: any authenticated app role (ADMIN / SUPER_ADMIN / PHOTOGRAPHER / …) -->
@if (showSideNav()) {
    <fuse-vertical-navigation
        class="pf-shell-sidebar print:hidden"
        [mode]="isScreenSmall ? 'over' : 'side'"
        [name]="'mainNavigation'"
        [navigation]="navigation?.default"
        [opened]="!isScreenSmall"
    >
        <!-- Navigation header hook -->
        <ng-container fuseVerticalNavigationContentHeader>
            <!-- PFL-1658: the sidebar used to duplicate the toolbar's <user> menu
                 with its own trigger (`<user [showAvatar]="false">`) plus a static
                 name/email block below it — two ways to reach the same "one user
                 menu" the ticket's Expected section calls for. Sign-in identity
                 (email) is still one click away via the toolbar dropdown's
                 "Signed in as" row (user.component.html), and the toolbar trigger
                 is the one that keeps working when the sidebar is collapsed or on
                 mobile, so it's the one kept. Header carries brand + acting org name. -->
            <div class="flex w-full items-center gap-3 p-4 pl-6">
                <!-- Logos: platform mark + acting-org mark side by side. The
                     org logo renders only for a Studio acting org with a logo
                     on its membership row (`sidebarOrgLogoUrl`); the System org
                     is the platform mark itself, so it shows alone there. -->
                <div class="flex shrink-0 items-center justify-center gap-2">
                    <img
                        class="w-12"
                        src="images/branding/PhotoFlow_512px.png"
                        alt="PhotoFlow"
                    />
                    @if (sidebarOrgLogoUrl) {
                        <img
                            class="h-12 w-12 rounded-full border border-white/20 object-cover"
                            [src]="sidebarOrgLogoUrl | absoluteMediaUrl"
                            [alt]="sidebarOrgName || 'Studio'"
                        />
                    }
                </div>
                <!-- Product + acting org (mirrors OrgPickerComponent's actingOrgId resolution) -->
                <div class="flex min-w-0 flex-col">
                    <span class="truncate text-base font-semibold leading-tight"
                        >PhotoFlow</span
                    >
                    @if (sidebarOrgName) {
                        <span class="text-secondary truncate text-xs">{{
                            sidebarOrgName
                        }}</span>
                    }
                    @if (_featureFlags.isEnabled(FEATURE_FLAGS.PORTAL_BETA_FREE)) {
                        <span
                            class="mt-1 inline-flex w-fit items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold uppercase leading-none text-primary"
                            >{{ 'common.betaFree' | transloco }}</span
                        >
                    }
                </div>
            </div>
        </ng-container>
        <!-- Navigation footer hook — deliberately empty (owner UI review
             2026-09-25): the faded logo watermark here was decorative
             chrome the design system doesn't call for (§12: no stray
             decoration on product chrome). No footer content; the sidebar
             ends at the last nav row. -->
    </fuse-vertical-navigation>
}

<!-- Wrapper -->
<div class="flex w-full min-w-0 flex-auto flex-col">
    <!-- Header -->
    @if (user) {
        <div
            class="pf-shell-topbar relative z-49 flex h-16 w-full flex-0 items-center px-4 md:px-6 print:hidden"
        >
            <!-- Navigation toggle button -->
            @if (showSideNav()) {
                <button
                    mat-icon-button
                    class="pf-header-icon-btn"
                    [attr.aria-label]="'common.toggleNavigation' | transloco"
                    (click)="toggleNavigation('mainNavigation')"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:bars-3'"></mat-icon>
                </button>
            } @else {
                <!-- No sidebar (collapsed/small screen): same dual-mark
                     pairing as the sidebar header above, minus the org name. -->
                <div class="flex shrink-0 items-center justify-center gap-2">
                    <img
                        class="w-20"
                        src="images/branding/PhotoFlow_512px.png"
                        alt="PhotoFlow"
                    />
                    @if (sidebarOrgLogoUrl) {
                        <img
                            class="h-8 w-8 rounded-full border border-white/20 object-cover"
                            [src]="sidebarOrgLogoUrl | absoluteMediaUrl"
                            [alt]="sidebarOrgName || 'Studio'"
                        />
                    }
                    @if (_featureFlags.isEnabled(FEATURE_FLAGS.PORTAL_BETA_FREE)) {
                        <span
                            class="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold uppercase leading-none text-primary"
                            >{{ 'common.betaFree' | transloco }}</span
                        >
                    }
                </div>
            }
            <!-- Components -->
            <div
                class="ml-auto flex items-center space-x-0.5 pl-2 sm:space-x-2"
            >
                <!-- C6: the indicator used to exist only in the `classic`
                     layout, which is not the app default. -->
                <connection-status></connection-status>
                <org-picker></org-picker>
                <languages></languages>
                <notifications></notifications>
                <user></user>
            </div>
        </div>
    }

    <!-- Content -->
    <main id="main-content" class="flex flex-auto flex-col">
        <!-- Quota usage warning banner (SOFT level during BETA) -->
        <app-usage-banner
            [warningLevel]="warningLevel"
            [albumsUsed]="albumsUsed"
            [albumsLimit]="albumsLimit"
            [photosUsed]="photosUsed"
            [photosLimit]="photosLimit"
        ></app-usage-banner>

        <!-- D3: quota-service unreachable, requests are going through unmeasured. -->
        @if (quotaDegraded) {
            <div class="px-4 pt-4 sm:px-6">
                <app-quota-banner [variant]="'degraded'"></app-quota-banner>
            </div>
        }

        <!-- D2: the last quota denial, with real usage/limit and an upgrade path.
             Cleared on navigation — it describes the action just attempted. -->
        @if (quotaDenial) {
            <div class="px-4 pt-4 sm:px-6">
                <app-quota-banner [quota]="quotaDenial"></app-quota-banner>
            </div>
        }

        @if (true) {
            <router-outlet></router-outlet>
        }
    </main>

    <!-- Footer -->
</div>

<!-- Quick chat -->
